import * as React from "react";
import { MediaQuery } from "react-responsive";
import "debounce";
import "tabbable";
import { useIsMounted } from "../../helpers/helper/hooks/use-is-mounted.mjs";
import { useResponsiveProps } from "../../helpers/helper/hooks/use-responsive-props.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
import { TableCell } from "../table-cell/table-cell.mjs";
import { TableRow } from "../table-row/table-row.mjs";
const TableContext = React.createContext({
  cellPadding: void 0
});
const Table = (props) => {
  const { responsiveProps, ...rest } = props;
  return responsiveProps ? /* @__PURE__ */ React.createElement(TableInnerResponsive, { ...rest, responsiveProps }) : /* @__PURE__ */ React.createElement(TableInner, { ...rest });
};
const TableInnerResponsive = (props) => {
  const { responsiveProps, ...rest } = props;
  const responsive = useResponsiveProps({
    responsive: responsiveProps
  });
  return /* @__PURE__ */ React.createElement(TableInner, { ...rest, ...responsive });
};
const TableInner = (props) => {
  const {
    break: breakpoint = "sm",
    responsive,
    hover,
    stickyHeader,
    className,
    bordered,
    maxHeight,
    minWidth,
    children,
    onScroll,
    elementRef,
    cellPadding,
    "aria-labelledby": ariaLabelledBy
  } = props;
  const { media } = useTheme();
  const tableContextValue = React.useMemo(() => ({ cellPadding }), [cellPadding]);
  const tableRef = React.useRef(null);
  const [isSticky, setIsSticky] = React.useState(false);
  const isMounted = useIsMounted();
  const BEM = () => {
    const classArray = ["table"];
    if (responsive) {
      classArray.push("table--responsive");
    }
    if (hover) {
      classArray.push("table--hover");
    }
    if (bordered) {
      classArray.push("table--bordered");
    }
    if (stickyHeader) {
      classArray.push("table--sticky-header-".concat(stickyHeader));
      if (isSticky) {
        classArray.push("is-sticky");
      }
    }
    if (className) {
      classArray.push(className);
    }
    return classArray.join(" ");
  };
  const currentBreakpoint = React.useMemo(() => {
    if (!responsive) {
      return 0;
    }
    return media.width[breakpoint].min;
  }, [media, breakpoint, responsive]);
  const handleScroll = (e) => {
    const { current } = tableRef;
    const isBroken = breakpoint && !window.matchMedia("(min-width: ".concat(currentBreakpoint, ")")).matches;
    if (current && !isBroken) {
      const currentIsSticky = current.scrollTop > 0;
      setIsSticky(currentIsSticky);
    }
    if (onScroll) {
      onScroll(e);
    }
  };
  const getCleanTable = React.useMemo(() => {
    const tableSections = React.Children.map(children, (child, childIndex) => {
      if (React.isValidElement(child) && (child.type === "thead" || child.type === "tbody")) {
        const rows = React.Children.map(child.props.children, (tr, trIndex) => {
          if (!React.isValidElement(tr)) {
            return null;
          }
          if (tr.type === "tr" || tr.type === TableRow) {
            const cells = React.Children.map(tr.props.children, (td) => {
              if (React.isValidElement(td) && (td.type === "td" || td.type === "th" || td.type === TableCell)) {
                return td;
              }
              return null;
            });
            return React.cloneElement(tr, {
              children: cells,
              key: trIndex
            });
          }
          return null;
        });
        return React.cloneElement(child, {
          children: rows,
          key: "" + childIndex
        });
      }
      return null;
    });
    return tableSections;
  }, [children]);
  const getTableDataCols = React.useMemo(() => {
    const cleanedTable = getCleanTable;
    const tables = [];
    const thead = [];
    let footer = null;
    cleanedTable.forEach((child) => {
      if (!React.isValidElement(child)) {
        return null;
      }
      React.Children.forEach(child.props.children, (tr, trIndex) => {
        if (!React.isValidElement(tr) || tr.props.type === "highlight-header") {
          return null;
        }
        let cellIndex = 0;
        const rowChildrenCount = React.Children.count(tr.props.children);
        const rowChildrenAsArray = React.Children.toArray(tr.props.children);
        if (child.type === "tbody" && !tables[trIndex]) {
          tables[trIndex] = [];
        }
        if ((tr.props["data-footer"] || tr.props.type === "footer") && rowChildrenCount <= 2) {
          footer = {
            cells: [rowChildrenAsArray[0], rowChildrenAsArray[rowChildrenCount - 1]],
            row: tr
          };
        } else {
          React.Children.forEach(rowChildrenAsArray, (td) => {
            if (!React.isValidElement(td)) {
              return null;
            }
            if (child.type === "thead") {
              thead.push(td);
              return;
            }
            if (tables[trIndex][cellIndex]) {
              do {
                cellIndex += 1;
              } while (tables[trIndex][cellIndex]);
            }
            const tableRepeat = td.props.rowSpan || 1;
            for (let i = 0; i < tableRepeat; i += 1) {
              const row = child.props.children[trIndex + i];
              if (!tables[trIndex + i]) {
                tables[trIndex + i] = [];
              }
              tables[trIndex + i][cellIndex] = {
                cells: [thead[cellIndex], td],
                row
              };
              if (td.props.colSpan) {
                for (let j = 1; j < td.props.colSpan; j += 1) {
                  tables[trIndex + i][cellIndex + j] = {
                    cells: [thead[cellIndex + j]],
                    row
                  };
                }
              }
            }
            if ((td.props["data-headless"] || td.props.headless) && td.props.colSpan) {
              for (let i = 0; i < td.props.colSpan; i++) {
                if (cellIndex + i !== cellIndex) {
                  delete tables[trIndex][cellIndex + i];
                }
              }
            }
          });
        }
      });
    });
    if (footer) {
      tables[tables.length - 1] = [];
      tables[tables.length - 1].push(footer);
    }
    return tables;
  }, [getCleanTable]);
  const getTableDataRows = React.useMemo(() => {
    const cleanedTable = getCleanTable;
    const tables = [];
    let rowIndex = 0;
    let footer = null;
    cleanedTable.forEach((child) => {
      if (!React.isValidElement(child)) {
        return null;
      }
      React.Children.forEach(child.props.children, (tr, trIndex) => {
        if (!React.isValidElement(tr)) {
          return null;
        }
        let tableIndex = 0;
        const rowChildrenCount = React.Children.count(tr.props.children);
        const rowChildrenAsArray = React.Children.toArray(tr.props.children);
        if (tr.props["data-footer"] || tr.props.type === "footer") {
          footer = {
            cells: [rowChildrenAsArray[0], rowChildrenAsArray[rowChildrenCount - 1]],
            row: tr
          };
        } else {
          React.Children.forEach(rowChildrenAsArray, (td, tdIndex) => {
            if (!React.isValidElement(td)) {
              return null;
            }
            if (tdIndex === 0 && td.props.colSpan) {
              tableIndex += td.props.colSpan - 1;
            }
            if (tdIndex === 0) {
              return;
            }
            if (!tables[tableIndex]) {
              tables[tableIndex] = [];
            }
            if (tables[tableIndex][rowIndex]) {
              do {
                tableIndex += 1;
              } while (tables[tableIndex][rowIndex]);
            }
            const tableRepeat = td.props.colSpan || 1;
            for (let i = 0; i < tableRepeat; i += 1) {
              if (!tables[tableIndex]) {
                tables[tableIndex] = [];
              }
              tables[tableIndex][rowIndex] = {
                cells: [rowChildrenAsArray[0], td],
                row: tr,
                type: child.type === "thead" ? "thead" : "tbody"
              };
              if (td.props.rowSpan) {
                for (let j = 1; j < td.props.rowSpan; j += 1) {
                  const row = child.props.children[trIndex + j];
                  tables[tableIndex][rowIndex + j] = {
                    cells: [row.props.children[0] || row.props.children],
                    row,
                    type: child.type === "thead" ? "thead" : "tbody"
                  };
                }
              }
              tableIndex += 1;
            }
          });
        }
        rowIndex += 1;
      });
    });
    if (footer) {
      tables[tables.length] = [];
      tables[tables.length - 1].push(footer);
    }
    return tables;
  }, [getCleanTable]);
  const isHeadless = (row) => {
    return row.row.props["data-headless"] || row.row.props.headless || row.cells[1] && (row.cells[1].props["data-headless"] || row.cells[1].props.headless);
  };
  const genThead = (thead, key) => {
    return /* @__PURE__ */ React.createElement("thead", { key }, thead);
  };
  const genTbody = (tbody, key, className2) => {
    return /* @__PURE__ */ React.createElement("tbody", { key, className: className2 }, tbody);
  };
  const getTableData = React.useMemo(() => {
    switch (responsive) {
      case "rows":
        return getTableDataRows;
      case "cols":
        return getTableDataCols;
      default:
        return [];
    }
  }, [getTableDataCols, getTableDataRows, responsive]);
  const getAltTable = React.useMemo(() => {
    const tablesData = getTableData;
    const newTable = [];
    tablesData.forEach((tableData, tableIndex) => {
      const thead = {
        children: [],
        type: "thead"
      };
      const bodyClassArray = [];
      const tbody = {
        children: [],
        className: void 0,
        type: "tbody"
      };
      newTable[tableIndex] = [];
      tableData.forEach((rowData, index) => {
        if (rowData.row.props.bodyClassName) {
          bodyClassArray.push(rowData.row.props.bodyClassName);
        }
        const headless = isHeadless(rowData);
        const colSpan = headless ? 2 : 0;
        const cell1 = !headless ? rowData.cells[0] : null;
        const cell2 = rowData.cells[1];
        let newCell1 = null;
        let newCell2 = null;
        if (cell1) {
          const newCell1Props = {
            colSpan: 0,
            key: 0
          };
          newCell1 = React.cloneElement(cell1, newCell1Props);
        }
        if (cell2) {
          let newCell2Props = {
            colSpan,
            key: 1
          };
          if (responsive === "cols") {
            newCell2Props = {
              colSpan,
              key: 1,
              rowSpan: cell2.props["data-headless"] || cell2.props.headless ? 1 : cell2.props.colSpan
            };
          }
          newCell2 = React.cloneElement(cell2, newCell2Props);
        }
        const newRowProps = {
          children: [newCell1, newCell2],
          key: index
        };
        const newRow = React.cloneElement(rowData.row, newRowProps);
        if (cell2 && !cell2.props.children) {
          return;
        }
        if (newRow.props["data-table-collapse"] || newRow.props.collapsible) {
          bodyClassArray.push("table-collapse__body");
        }
        if (rowData.type === "thead") {
          thead.children.push(newRow);
        } else {
          tbody.children.push(newRow);
        }
      });
      tbody.className = bodyClassArray.join(" ");
      newTable[tableIndex] = [thead, tbody];
    });
    const blocks = newTable.reduce((arr, curr) => {
      return arr.concat(curr);
    }, []).map((block, i) => {
      if (block.type === "thead") {
        return genThead(block.children, i);
      } else {
        return genTbody(block.children, i, block.className);
      }
    });
    return /* @__PURE__ */ React.createElement("table", null, blocks);
  }, [getTableData, responsive]);
  const getDynamicTable = () => {
    return /* @__PURE__ */ React.createElement(MediaQuery, { minWidth: currentBreakpoint }, (matches) => matches ? getDefaultTable(true) : /* @__PURE__ */ React.createElement("div", { className: "table__responsive" }, responsive && getAltTable));
  };
  const getDefaultTable = (hasBreakProp) => {
    const tableContent = hasBreakProp ? getCleanTable : children;
    return /* @__PURE__ */ React.createElement("div", { className: "table__original" }, /* @__PURE__ */ React.createElement(
      "table",
      {
        style: minWidth ? {
          minWidth: typeof minWidth === "string" ? minWidth : "".concat(minWidth, "px")
        } : void 0
      },
      tableContent
    ));
  };
  const tableStyle = React.useMemo(() => {
    let styles = {};
    if (maxHeight) {
      styles = {
        ...styles,
        maxHeight
      };
    }
    return styles;
  }, [maxHeight]);
  React.useEffect(() => {
    if (elementRef && tableRef.current) {
      elementRef(tableRef.current);
    }
  }, [elementRef, tableRef]);
  return /* @__PURE__ */ React.createElement(TableContext.Provider, { value: tableContextValue }, /* @__PURE__ */ React.createElement(
    "section",
    {
      "aria-labelledby": ariaLabelledBy,
      tabIndex: ariaLabelledBy ? 0 : void 0,
      className: BEM(),
      style: tableStyle,
      ref: tableRef,
      onScroll: handleScroll
    },
    isMounted && responsive ? getDynamicTable() : getDefaultTable(false)
  ));
};
export {
  Table,
  TableContext
};
